import React, { useEffect, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Archive, RotateCcw, Trash2 } from "lucide-react";

const AdminDeleted = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/deleted-merchants");
      setMerchants(r.data);
    } catch (e) { toast.error(arabicError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const restore = async (m) => {
    const label = [m.first_name, m.last_name].filter(Boolean).join(" ") || m.store_name;
    if (!window.confirm(`استعادة "${label}"؟`)) return;
    setBusyId(m.id);
    try {
      await api.post(`/admin/merchants/${m.id}/restore`);
      toast.success("تمت الاستعادة");
      setMerchants((prev) => prev.filter((x) => x.id !== m.id));
    } catch (e) { toast.error(arabicError(e)); }
    finally { setBusyId(null); }
  };

  const permaDelete = async (m) => {
    const label = [m.first_name, m.last_name].filter(Boolean).join(" ") || m.store_name;
    const msg = `حذف نهائي لـ"${label}"؟\nسيتم محو جميع صوره وحجوزاته ومحادثاته من قاعدة البيانات نهائياً. لا يمكن التراجع.`;
    if (!window.confirm(msg)) return;
    setBusyId(m.id);
    try {
      await api.delete(`/admin/merchants/${m.id}/permanent`);
      toast.success("تم الحذف النهائي");
      setMerchants((prev) => prev.filter((x) => x.id !== m.id));
    } catch (e) { toast.error(arabicError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-deleted-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">التجار المحذوفون</h1>
        <p className="text-[#666666] mt-1">يمكنك استعادة أي تاجر أو حذفه نهائياً</p>
      </div>

      {loading ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-8 text-center text-[#666666]">جارٍ التحميل...</div>
      ) : merchants.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-12 text-center">
          <Archive className="mx-auto text-[#7AB2B2] mb-3" size={48} />
          <p className="text-[#666666] text-lg">لا يوجد تجار محذوفون</p>
        </div>
      ) : (
        <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden">
          <div className="md:hidden bg-[#EBF4F6] text-[#09637E] text-xs font-bold px-3 py-2 border-b border-[#7AB2B2]" data-testid="table-swipe-hint">← اسحب الجدول يميناً ويساراً لعرض بقية الأعمدة والإجراءات</div>
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead className="bg-[#EBF4F6]">
                <tr>
                  <th className="p-4 text-sm font-bold">البريد</th>
                  <th className="p-4 text-sm font-bold">الاسم</th>
                  <th className="p-4 text-sm font-bold">المحل</th>
                  <th className="p-4 text-sm font-bold">المحافظة</th>
                  <th className="p-4 text-sm font-bold">الهاتف</th>
                  <th className="p-4 text-sm font-bold">تاريخ الحذف</th>
                  <th className="p-4 text-sm font-bold">الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {merchants.map((m) => (
                  <tr key={m.id} className="border-t border-[#EBF4F6]" data-testid={`deleted-row-${m.id}`}>
                    <td className="p-4 text-[#333333]">{m.email}</td>
                    <td className="p-4 text-[#333333]">{[m.first_name, m.last_name].filter(Boolean).join(" ") || "-"}</td>
                    <td className="p-4 font-bold text-[#333333]">{m.store_name}</td>
                    <td className="p-4 text-[#666666]">{m.city}</td>
                    <td className="p-4 text-[#666666] text-sm" dir="ltr">{m.phone_number || "-"}</td>
                    <td className="p-4 text-[#666666] text-sm">{m.deleted_at ? new Date(m.deleted_at).toLocaleString("ar-SY") : "-"}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => restore(m)} disabled={busyId === m.id} data-testid={`restore-${m.id}`}
                          className="h-10 px-3 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1 whitespace-nowrap disabled:opacity-60">
                          <RotateCcw size={16} /> استعادة
                        </button>
                        <button onClick={() => permaDelete(m)} disabled={busyId === m.id} data-testid={`perma-delete-${m.id}`}
                          className="h-10 px-3 rounded-lg border-2 border-red-500 text-red-500 hover:bg-red-50 font-bold text-sm flex items-center gap-1 whitespace-nowrap disabled:opacity-60">
                          <Trash2 size={16} /> حذف نهائي
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeleted;
